import React, { useState } from 'react';
import {
  Zap,
  Timer,
  Bell,
  TrendingUp,
  MousePointerClick,
  Mail,
  Database,
  PieChart,
  BookOpen,
  Building2,
  ArrowRight,
  ChevronRight,
  Check,
  Settings,
  Palette,
  Calendar,
  Clock,
  Shield,
} from 'lucide-react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { usePageTitle } from '@/hooks/usePageTitle';
import PortalNav from '@/components/PortalNav';
import PortalFooter from '@/components/PortalFooter';
import {
  portalPageStyles,
  portalHeroStyles,
  portalValuePropStyles,
  portalFeatureStyles,
  portalStepsStyles,
  portalAudienceStyles,
  portalFaqStyles,
  portalTestimonialStyles,
  portalPricingStyles,
  portalFinderStyles,
} from '@/styles/portalPageStyles';

interface School {
  subdomain: string;
  name: string;
  contactEmail: string | null;
}

// Data for features section
const features = [
  {
    icon: Zap,
    title: 'Teachers Save Time',
    description: 'Log incidents and recognitions in seconds, not minutes. No paperwork, no disruption to your teaching.',
  },
  {
    icon: Timer,
    title: 'Less Admin Work',
    description: 'Automated family digests eliminate follow-up tasks. Reports generate themselves.',
  },
  {
    icon: Bell,
    title: 'Families Stay Informed',
    description: 'Daily or weekly digest emails keep families in the loop—without mid-day interruptions to teachers.',
  },
  {
    icon: TrendingUp,
    title: 'Patterns Become Visible',
    description: 'Spot trends, recognize top students, and make data-driven behavior decisions.',
  },
];

// Data for how it works section
const steps = [
  {
    icon: MousePointerClick,
    title: 'Log Behavior',
    description: 'Tap student photo',
  },
  {
    icon: Mail,
    title: 'Family Notified',
    description: 'Daily/weekly digest',
  },
  {
    icon: Database,
    title: 'Data Tracked',
    description: 'Auto-recorded',
  },
  {
    icon: PieChart,
    title: 'Patterns Identified',
    description: 'Reports ready',
  },
];

// Data for FAQ section
const faqs = [
  {
    question: 'Does Checkademic track positive behaviors?',
    answer:
      'Yes! Schools can enable positive behavior tracking to recognize students alongside incident tracking. Use the same quick workflow for both—and optionally add a points system for gamification.',
  },
  {
    question: 'How does the points system work?',
    answer:
      'Schools can optionally enable points to reward positive behavior. Students earn points for recognitions and lose points for incidents—net scores are calculated automatically. Point values are fully customizable per behavior category, and teachers can override them on the fly.',
  },
  {
    question: 'How are families notified?',
    answer:
      'Via daily or weekly email digests—your choice. This keeps families informed without flooding them with mid-day alerts that interrupt teachers.',
  },
  {
    question: 'What data can administrators see?',
    answer:
      "Everything—every incident, every recognition, every staff member's activity, complete audit trails. Export reports for board meetings.",
  },
  {
    question: 'Can staff document observations about students?',
    answer:
      'Yes! Staff can create categorized notes (behavioral, medical, academic, family/legal, general) on any student. Notes support visibility controls so sensitive information stays admin-only, and individual notes can be shared with families through the parent portal and digest emails.',
  },
  {
    question: 'How customizable is Checkademic?',
    answer:
      'Highly. Schools can customize branding (colors, logo), notification schedules, behavior categories, edit windows, attendance settings, and much more.',
  },
];

// Data for testimonials (placeholders)
const testimonials = [
  {
    quote: 'I used to spend 20 minutes after class documenting behavior. Now it takes seconds.',
    author: 'High School Teacher',
    role: '10+ years experience',
  },
  {
    quote: "No one can claim they didn't know anymore. The daily digests keep everyone accountable.",
    author: 'Middle School Principal',
    role: 'Administration',
  },
  {
    quote: 'We finally have data on both incidents and recognitions. Board meetings are so much easier when you can show the full picture.',
    author: 'District Administrator',
    role: "Superintendent's Office",
  },
];

// Teacher benefits list
const teacherBenefits = [
  'Log incidents or recognitions without leaving your lesson',
  'Photo-based student selection',
  'Predefined behavior categories',
  'No login friction - stay logged in',
  'Document student observations with categorized notes',
];

// Admin benefits list
const adminBenefits = [
  'Full visibility into incidents and recognitions',
  'Audit trails for every action',
  'Automated family communication',
  'Export reports for board meetings',
  'Confidential student notes with visibility controls',
];

// Configuration features
const configFeatures = [
  {
    icon: Palette,
    title: 'Custom Branding',
    description: 'Your colors, your logo. Make Checkademic look like your school.',
  },
  {
    icon: Calendar,
    title: 'Flexible Scheduling',
    description: 'Traditional periods, block scheduling, or elementary homerooms—we adapt to you.',
  },
  {
    icon: Clock,
    title: 'Notification Timing',
    description: 'Daily or weekly digests. Choose when families receive updates.',
  },
  {
    icon: Shield,
    title: 'Access Controls',
    description: 'Control who sees what. Configure teacher and admin permissions.',
  },
];

function PortalHome() {
  usePageTitle('Find Your School');
  const { isMobile } = useMediaQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [schools, setSchools] = useState<School[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const [hoveredConfig, setHoveredConfig] = useState<number | null>(null);
  const [hoveredSchool, setHoveredSchool] = useState<string | null>(null);

  const handleSearch = async () => {
    setError(null);

    if (searchTerm.trim().length < 2) {
      setError('Please enter at least 2 characters');
      return;
    }

    setSearching(true);
    try {
      const response = await fetch(`/api/portal/search-schools?q=${encodeURIComponent(searchTerm.trim())}`);

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setSchools(data.schools || []);

      if (data.schools.length === 0) {
        setError(
          'No schools found. Try a different search term, or your school may not be enrolled with Checkademic yet.'
        );
      }
    } catch (error) {
      console.error('Search failed:', error);
      setError('Unable to search schools. Please try again.');
      setSchools([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSchoolSelect = (subdomain: string) => {
    const protocol = window.location.protocol;
    const domain = window.location.hostname.includes('localhost') ? 'localhost:5173' : window.location.hostname;
    window.location.href = `${protocol}//${subdomain}.${domain}/login`;
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const scrollToFinder = () => {
    document.getElementById('school-finder')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div style={portalPageStyles.pageContainer}>
      <PortalNav currentPage="home" />

      {/* Hero Section */}
      <section style={portalHeroStyles.container}>
        <div style={isMobile ? portalHeroStyles.contentMobile : portalHeroStyles.content}>
          {/* Text Column */}
          <div style={portalHeroStyles.textColumn}>
            <h1 style={isMobile ? portalHeroStyles.headlineMobile : portalHeroStyles.headline}>
              Stop Wasting Time on Paperwork. Start Managing Behavior.
            </h1>
            <p style={isMobile ? portalHeroStyles.subheadlineMobile : portalHeroStyles.subheadline}>
              Checkademic lets teachers log behavior—positive and negative—and document observations in seconds. Families
              get daily digests. Administrators track everything automatically.
            </p>
            <button
              onClick={scrollToFinder}
              style={isMobile ? portalHeroStyles.ctaButtonMobile : portalHeroStyles.ctaButton}
            >
              Find Your School
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Hero Image (Desktop only) */}
          {!isMobile && (
            <div style={portalHeroStyles.graphicColumn}>
              <img
                src="/classroom-hero.jpg"
                alt="Teacher working with engaged students in a classroom"
                width={480}
                height={320}
                style={portalHeroStyles.heroImage}
                fetchPriority="high"
              />
            </div>
          )}
        </div>
      </section>

      {/* Value Proposition Strip */}
      <section style={portalValuePropStyles.container}>
        <div style={isMobile ? portalValuePropStyles.contentMobile : portalValuePropStyles.content}>
          <div style={portalValuePropStyles.box}>
            <div style={portalValuePropStyles.number}>Seconds</div>
            <div style={portalValuePropStyles.label}>Not minutes, to log</div>
          </div>
          <div style={portalValuePropStyles.box}>
            <div style={portalValuePropStyles.number}>Automatic</div>
            <div style={portalValuePropStyles.label}>Family digests</div>
          </div>
          <div style={portalValuePropStyles.box}>
            <div style={portalValuePropStyles.number}>Complete</div>
            <div style={portalValuePropStyles.label}>Audit trail</div>
          </div>
        </div>
      </section>

      <div style={portalPageStyles.container}>
        {/* Features Section */}
        <section style={portalPageStyles.section}>
          <h2 style={portalPageStyles.sectionTitle}>Why Schools Switch to Checkademic</h2>
          <div style={isMobile ? portalFeatureStyles.gridMobile : portalFeatureStyles.grid}>
            {features.map((feature, index) => {
              const Icon = feature.icon;
              const isHovered = hoveredFeature === index;
              return (
                <div
                  key={index}
                  style={{
                    ...portalFeatureStyles.card,
                    ...(isHovered ? portalFeatureStyles.cardHover : {}),
                  }}
                  onMouseEnter={() => setHoveredFeature(index)}
                  onMouseLeave={() => setHoveredFeature(null)}
                >
                  <div style={portalFeatureStyles.iconContainer}>
                    <Icon size={24} style={portalFeatureStyles.icon} />
                  </div>
                  <h3 style={portalFeatureStyles.title}>{feature.title}</h3>
                  <p style={portalFeatureStyles.description}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* How It Works Section */}
        <section style={portalPageStyles.section}>
          <h2 style={portalPageStyles.sectionTitle}>Behavior Tracking in 4 Simple Steps</h2>
          <div style={isMobile ? portalStepsStyles.containerMobile : portalStepsStyles.container}>
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={index}>
                  <div style={portalStepsStyles.item}>
                    <div style={portalStepsStyles.number}>
                      <Icon size={28} style={portalStepsStyles.icon} />
                    </div>
                    <h3 style={portalStepsStyles.title}>{step.title}</h3>
                    <p style={portalStepsStyles.description}>{step.description}</p>
                  </div>
                  {index < steps.length - 1 && !isMobile && (
                    <div style={portalStepsStyles.arrow}>
                      <ArrowRight size={24} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </section>

        {/* Dual Audience Section */}
        <section style={portalPageStyles.section}>
          <div style={isMobile ? portalAudienceStyles.gridMobile : portalAudienceStyles.grid}>
            {/* Teachers Card */}
            <div style={portalAudienceStyles.card}>
              <div style={portalAudienceStyles.cardHeader}>
                <BookOpen size={28} style={portalAudienceStyles.icon} />
                <h3 style={portalAudienceStyles.title}>Built for Teachers</h3>
              </div>
              <ul style={portalAudienceStyles.list}>
                {teacherBenefits.map((benefit, index) => (
                  <li key={index} style={portalAudienceStyles.listItem}>
                    <Check size={18} style={portalAudienceStyles.checkIcon} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Administrators Card */}
            <div style={portalAudienceStyles.card}>
              <div style={portalAudienceStyles.cardHeader}>
                <Building2 size={28} style={portalAudienceStyles.icon} />
                <h3 style={portalAudienceStyles.title}>Built for Administrators</h3>
              </div>
              <ul style={portalAudienceStyles.list}>
                {adminBenefits.map((benefit, index) => (
                  <li key={index} style={portalAudienceStyles.listItem}>
                    <Check size={18} style={portalAudienceStyles.checkIcon} />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Configuration Section */}
        <section style={portalPageStyles.section}>
          <h2 style={portalPageStyles.sectionTitle}>
            <Settings size={28} style={{ display: 'inline', marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Highly Configurable
          </h2>
          <p
            style={{
              textAlign: 'center',
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              maxWidth: '600px',
              margin: '0 auto 2rem',
            }}
          >
            Every school is different. Checkademic adapts to your workflow, not the other way around.
          </p>
          <div style={isMobile ? portalFeatureStyles.gridMobile : portalFeatureStyles.grid}>
            {configFeatures.map((feature, index) => {
              const Icon = feature.icon;
              const isHovered = hoveredConfig === index;
              return (
                <div
                  key={index}
                  style={{
                    ...portalFeatureStyles.card,
                    ...(isHovered ? portalFeatureStyles.cardHover : {}),
                  }}
                  onMouseEnter={() => setHoveredConfig(index)}
                  onMouseLeave={() => setHoveredConfig(null)}
                >
                  <div style={portalFeatureStyles.iconContainer}>
                    <Icon size={24} style={portalFeatureStyles.icon} />
                  </div>
                  <h3 style={portalFeatureStyles.title}>{feature.title}</h3>
                  <p style={portalFeatureStyles.description}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ Section */}
        <section style={portalPageStyles.section}>
          <h2 style={portalPageStyles.sectionTitle}>Common Questions</h2>
          <div style={portalFaqStyles.container}>
            {faqs.map((faq, index) => (
              <div key={index} style={portalFaqStyles.item}>
                <h3 style={portalFaqStyles.question}>{faq.question}</h3>
                <p style={portalFaqStyles.answer}>{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Testimonials Section */}
        <section style={portalPageStyles.section}>
          <h2 style={portalPageStyles.sectionTitle}>What Schools Are Saying</h2>
          <div style={isMobile ? portalTestimonialStyles.gridMobile : portalTestimonialStyles.grid}>
            {testimonials.map((testimonial, index) => (
              <div key={index} style={portalTestimonialStyles.card}>
                <div style={portalTestimonialStyles.exampleBadge}>Example</div>
                <div style={portalTestimonialStyles.quoteMark}>"</div>
                <p style={portalTestimonialStyles.quote}>{testimonial.quote}</p>
                <div style={portalTestimonialStyles.attribution}>{testimonial.author}</div>
                <div style={portalTestimonialStyles.role}>{testimonial.role}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Pricing Section */}
        <section style={portalPageStyles.section}>
          <div style={isMobile ? portalPricingStyles.containerMobile : portalPricingStyles.container}>
            <h2 style={portalPricingStyles.title}>Pricing That Fits Your School</h2>
            <p style={portalPricingStyles.description}>
              Whether you're a single school or entire district, we have a plan that works for you.
              Starting at $750/year for schools with up to 50 students.
            </p>
            <a href="/pricing" style={portalPricingStyles.ctaButton}>
              View Pricing
              <ArrowRight size={18} />
            </a>
            <p style={portalPricingStyles.secondaryText}>Free demo available for qualified schools</p>
          </div>
        </section>

        {/* School Finder */}
        <section
          id="school-finder"
          style={{
            ...(isMobile ? portalFinderStyles.containerMobile : portalFinderStyles.container),
            scrollMarginTop: '80px',
          }}
        >
          <h2 style={portalPageStyles.sectionTitle}>Find Your School</h2>
          <div style={isMobile ? portalFinderStyles.searchBoxMobile : portalFinderStyles.searchBox}>
            <input
              type="text"
              placeholder="Search by school name or subdomain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              style={isMobile ? portalFinderStyles.inputMobile : portalFinderStyles.input}
            />
            <button
              onClick={handleSearch}
              disabled={searching}
              style={isMobile ? portalFinderStyles.buttonMobile : portalFinderStyles.button}
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {error && <div style={portalFinderStyles.error}>{error}</div>}

          {schools.length > 0 && (
            <div style={isMobile ? portalFinderStyles.resultsGridMobile : portalFinderStyles.resultsGrid}>
              {schools.map((school) => {
                const isHovered = hoveredSchool === school.subdomain;
                return (
                  <div
                    key={school.subdomain}
                    style={{
                      ...portalFinderStyles.schoolCard,
                      ...(isHovered ? portalFinderStyles.schoolCardHover : {}),
                    }}
                    onClick={() => handleSchoolSelect(school.subdomain)}
                    onMouseEnter={() => setHoveredSchool(school.subdomain)}
                    onMouseLeave={() => setHoveredSchool(null)}
                  >
                    <div style={portalFinderStyles.schoolName}>{school.name}</div>
                    <div style={portalFinderStyles.schoolSubdomain}>{school.subdomain}.checkademic.com</div>
                    {school.contactEmail && (
                      <a
                        href={`mailto:${school.contactEmail}?subject=${encodeURIComponent(`Help with ${school.name} Portal`)}`}
                        onClick={(e) => e.stopPropagation()}
                        style={portalFinderStyles.schoolContact}
                      >
                        Contact: {school.contactEmail}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <PortalFooter />
    </div>
  );
}

export default PortalHome;
